import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bookmark, BookOpen, Check, Heart, MessageCircle, Pencil, Share2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import { EmptyState } from "@/components/empty-state";
import { TipWriterDialog } from "@/components/tip-writer-dialog";
import { TranslatePanel } from "@/components/translate-panel";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { CommentModerationControls } from "@/components/comment-moderation-controls";
import { supabase } from "@/integrations/supabase/client";
import { MembersOnlyGate } from "@/components/members-only-gate";
import { useAuth } from "@/lib/auth-context";
import { fetchChapters, fetchStoryComments, fetchStoryDetail } from "@/lib/queries";
import { fetchStoryTags } from "@/lib/tags";
import { TagChip } from "@/components/tag-chip";

const SITE_URL = "https://quibblies-inkwell.lovable.app";

export const Route = createFileRoute("/story/$storyId")({
  loader: async ({ params }) => {
    try {
      const story = await fetchStoryDetail(params.storyId);
      return { story };
    } catch {
      return { story: null };
    }
  },
  head: ({ params, loaderData }) => {
    const story = loaderData?.story as
      | {
          title?: string | null;
          description?: string | null;
          cover_url?: string | null;
          published_at?: string | null;
          profiles?: { display_name?: string | null; username?: string | null } | null;
          pen_names?: { name?: string | null } | null;
        }
      | null
      | undefined;

    const url = `${SITE_URL}/story/${params.storyId}`;
    const title = story?.title
      ? `${story.title} — Quibblies`
      : "Read a publication — Quibblies";
    const description =
      story?.description?.slice(0, 155) ??
      "Read original writing on Quibblies — fiction, personal experiences, reflections and poetry, reviewed by moderators.";
    const author =
      story?.pen_names?.name ?? story?.profiles?.display_name ?? story?.profiles?.username ?? undefined;
    const image =
      story?.cover_url && story.cover_url.startsWith("https://") ? story.cover_url : undefined;

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: story?.title ?? "Read a publication — Quibblies" },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        ...(image
          ? [
              { property: "og:image", content: image },
              { name: "twitter:image", content: image },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: story
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                headline: story.title ?? "Publication",
                description,
                url,
                ...(image ? { image } : {}),
                ...(story.published_at ? { datePublished: story.published_at } : {}),
                ...(author ? { author: { "@type": "Person", name: author } } : {}),
                publisher: { "@type": "Organization", name: "Quibblies" },
              }),
            },
          ]
        : undefined,
    };
  },
  component: StoryPage,
});



const commentSchema = z.string().trim().min(2, "Write a little more").max(2000);

function StoryPage() {
  const { storyId } = Route.useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [comment, setComment] = React.useState("");
  const [editingCommentId, setEditingCommentId] = React.useState<string | null>(null);
  const [editingCommentBody, setEditingCommentBody] = React.useState("");

  const story = useQuery({
    queryKey: ["story", storyId],
    queryFn: () => fetchStoryDetail(storyId),
  });
  const chapters = useQuery({
    queryKey: ["chapters", storyId],
    queryFn: () => fetchChapters(storyId),
  });
  const tags = useQuery({
    queryKey: ["story-tags", storyId],
    queryFn: () => fetchStoryTags(storyId),
  });
  const comments = useQuery({
    queryKey: ["comments", storyId],
    queryFn: () => fetchStoryComments(storyId),
  });

  const liked = useQuery({
    queryKey: ["like", storyId, user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("likes")
        .select("story_id")
        .eq("story_id", storyId)
        .eq("user_id", user!.id)
        .maybeSingle();
      return Boolean(data);
    },
    enabled: Boolean(user),
  });

  const bookmarked = useQuery({
    queryKey: ["bookmark", storyId, user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("bookmarks")
        .select("story_id")
        .eq("story_id", storyId)
        .eq("user_id", user!.id)
        .maybeSingle();
      return Boolean(data);
    },
    enabled: Boolean(user),
  });

  const toggleLike = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Sign in to like publications");
      if (liked.data) {
        const { error } = await supabase
          .from("likes")
          .delete()
          .eq("story_id", storyId)
          .eq("user_id", user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("likes")
          .insert({ story_id: storyId, user_id: user.id });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["like", storyId] });
      void queryClient.invalidateQueries({ queryKey: ["story", storyId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const toggleBookmark = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Sign in to save publications");
      if (bookmarked.data) {
        const { error } = await supabase
          .from("bookmarks")
          .delete()
          .eq("story_id", storyId)
          .eq("user_id", user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("bookmarks")
          .insert({ story_id: storyId, user_id: user.id });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["bookmark", storyId] });
      void queryClient.invalidateQueries({ queryKey: ["library"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const postComment = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Sign in to comment");
      const parsed = commentSchema.safeParse(comment);
      if (!parsed.success) throw new Error(parsed.error.issues[0]!.message);
      const { error } = await supabase
        .from("comments")
        .insert({ story_id: storyId, author_id: user.id, body: parsed.data });
      if (error) throw error;
    },
    onSuccess: () => {
      setComment("");
      void queryClient.invalidateQueries({ queryKey: ["comments", storyId] });
      void queryClient.invalidateQueries({ queryKey: ["story", storyId] });
      toast.success("Comment posted");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const editComment = useMutation({
    mutationFn: async () => {
      if (!user || !editingCommentId) throw new Error("Choose a comment to edit");
      const parsed = commentSchema.safeParse(editingCommentBody);
      if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Check your comment");
      const { error } = await supabase
        .from("comments")
        .update({ body: parsed.data })
        .eq("id", editingCommentId)
        .eq("author_id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      setEditingCommentId(null);
      setEditingCommentBody("");
      void queryClient.invalidateQueries({ queryKey: ["comments", storyId] });
      toast.success("Comment updated");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteComment = useMutation({
    mutationFn: async (commentId: string) => {
      if (!user) throw new Error("Sign in to delete your comment");
      const { error } = await supabase
        .from("comments")
        .delete()
        .eq("id", commentId)
        .eq("author_id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["comments", storyId] });
      void queryClient.invalidateQueries({ queryKey: ["story", storyId] });
      toast.success("Comment deleted");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  async function sharePublication() {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: story.data?.title ?? "Quibblies publication", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Publication link copied");
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      toast.error("Could not share this publication");
    }
  }

  // Reading is members-only, and the database returns nothing to signed-out
  // visitors — say so plainly instead of showing an empty publication.
  if (!user) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
        <MembersOnlyGate description="Sign in to read this publication, its chapters and its comments. Quibblies is an 18+ community and writing is visible to members only." />
      </div>
    );
  }

  if (story.isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <Skeleton className="h-64 rounded-3xl" />
      </div>
    );
  }

  if (!story.data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
        <EmptyState
          icon={BookOpen}
          title="Publication unavailable"
          description="This publication may be a draft, awaiting review, or removed."
        />
      </div>
    );
  }

  const data = story.data as typeof story.data & {
    content_types: { name: string; slug: string; is_personal: boolean } | null;
    pen_names: { name: string } | null;
  };
  const contentType = data.content_types;
  const penName = data.pen_names?.name ?? null;
  const author = data.profiles as unknown as
    | { username: string; display_name: string; avatar_url: string | null }
    | null;

  return (
    <article className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <header>
        <div className="flex flex-wrap items-center gap-2">
          {contentType ? (
            <span className="rounded-full bg-primary/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground">
              {contentType.name}
            </span>
          ) : null}
          {data.categories ? (
            <span className="text-xs uppercase tracking-wide text-primary-glow">
              {(data.categories as unknown as { name: string }).name}
            </span>
          ) : null}
        </div>
        <h1 className="mt-2 font-display text-4xl leading-tight text-foreground sm:text-5xl">
          {data.title}
        </h1>
        {author ? (
          <p className="mt-3 text-sm text-muted-foreground">
            by{" "}
            <Link
              to="/u/$username"
              params={{ username: author.username }}
              className="text-primary-glow hover:underline"
            >
              {penName ?? author.display_name}
            </Link>{" "}
            · {data.reading_minutes} min read · {data.word_count} words
          </p>
        ) : null}
        {data.description ? (
          <p className="mt-5 leading-relaxed text-muted-foreground">{data.description}</p>
        ) : null}

        {(tags.data ?? []).length > 0 ? (
          <section className="mt-5">
            <h2 className="text-xs uppercase tracking-wide text-muted-foreground">Tags</h2>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {tags.data!.map((tag) => (
                <TagChip key={tag.id} tag={tag} size="md" />
              ))}
            </div>
          </section>
        ) : null}

        <div className="mt-6 flex flex-wrap gap-2">
          <Button
            variant={liked.data ? "hero" : "soft"}
            size="sm"
            onClick={() => toggleLike.mutate()}
          >
            <Heart className="h-4 w-4" aria-hidden="true" />
            {data.like_count} likes
          </Button>
          <Button
            variant={bookmarked.data ? "hero" : "soft"}
            size="sm"
            onClick={() => toggleBookmark.mutate()}
          >
            <Bookmark className="h-4 w-4" aria-hidden="true" />
            {bookmarked.data ? "Saved" : "Save"}
          </Button>
          <Button variant="soft" size="sm" onClick={() => void sharePublication()}>
            <Share2 className="h-4 w-4" aria-hidden="true" />
            Share
          </Button>
          {author && (data.profiles as unknown as { support_enabled?: boolean })
            ?.support_enabled ? (
            <TipWriterDialog
              writerId={data.author_id}
              writerName={penName ?? author.display_name}
              publicationId={data.id}
            />
          ) : null}
        </div>
      </header>

      <div className="mt-10">
        <TranslatePanel
          publicationId={data.id}
          originalLanguage={data.original_language ?? "en"}
        >
          <div className="mt-6 space-y-10">
            {(chapters.data ?? []).map((chapter) => (
              <section key={chapter.id}>
                {(chapters.data ?? []).length > 1 ? (
                  <h2 className="mb-4 font-display text-2xl text-foreground">{chapter.title}</h2>
                ) : null}
                <div className="space-y-5 font-display text-lg leading-[1.8] text-foreground/90">
                  {chapter.content.split(/\n{2,}/).map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </TranslatePanel>
      </div>

      {contentType?.is_personal ? (
        <aside className="mt-12 rounded-2xl border border-primary/40 bg-accent/50 p-5 text-sm text-muted-foreground">
          This publication is presented as personal writing rather than fiction. Please read with
          care, and respect the privacy of everyone mentioned in it.
        </aside>
      ) : null}

      {data.author_notes ? (
        <aside className="mt-12 rounded-2xl border border-border/70 bg-surface/50 p-5 text-sm text-muted-foreground">
          <strong className="text-foreground">Author's note — </strong>
          {data.author_notes}
        </aside>
      ) : null}

      <section className="mt-14">
        <h2 className="font-display text-2xl text-foreground">Comments</h2>
        {data.comments_enabled ? (
          <div className="mt-4">
            <Textarea
              rows={3}
              maxLength={2000}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder={user ? "Share what you thought…" : "Sign in to join the conversation"}
              disabled={!user}
            />
            <Button
              className="mt-3"
              variant="soft"
              disabled={!user || postComment.isPending}
              onClick={() => postComment.mutate()}
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" />
              Post comment
            </Button>
          </div>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">
            The author has turned comments off for this publication.
          </p>
        )}

        <div className="mt-8 space-y-4">
          {(comments.data ?? [])
            .filter((item) => !item.removed)
            .map((item) => (
              <article
                key={item.id}
                className="rounded-2xl border border-border/70 bg-surface/50 p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  {item.profiles?.username ? (
                    <Link
                      to="/u/$username"
                      params={{ username: item.profiles.username }}
                      className="text-sm text-foreground hover:text-primary-glow hover:underline"
                    >
                      {item.profiles.display_name ?? item.profiles.username}
                    </Link>
                  ) : (
                    <span className="text-sm text-foreground">Member</span>
                  )}
                  <time className="text-xs text-muted-foreground" dateTime={item.created_at}>
                    {new Date(item.created_at).toLocaleDateString()}
                  </time>
                </div>
                {editingCommentId === item.id ? (
                  <div className="mt-3 space-y-2">
                    <Textarea
                      aria-label="Edit comment"
                      rows={3}
                      maxLength={2000}
                      value={editingCommentBody}
                      onChange={(event) => setEditingCommentBody(event.target.value)}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" disabled={editComment.isPending} onClick={() => editComment.mutate()}>
                        <Check aria-hidden="true" /> Save
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingCommentId(null);
                          setEditingCommentBody("");
                        }}
                      >
                        <X aria-hidden="true" /> Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
                    {user?.id === item.author_id ? (
                      <div className="mt-3 flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label="Edit comment"
                          onClick={() => {
                            setEditingCommentId(item.id);
                            setEditingCommentBody(item.body);
                          }}
                        >
                          <Pencil aria-hidden="true" /> Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label="Delete comment"
                          disabled={deleteComment.isPending}
                          onClick={() => deleteComment.mutate(item.id)}
                        >
                          <Trash2 aria-hidden="true" /> Delete
                        </Button>
                      </div>
                    ) : (
                      <CommentModerationControls
                        commentId={item.id}
                        storyId={storyId}
                        authorId={item.author_id}
                        authorName={item.profiles?.display_name ?? item.profiles?.username ?? "this member"}
                        viewerId={user?.id ?? null}
                      />
                    )}

                  </>
                )}
              </article>
            ))}
        </div>
      </section>
    </article>
  );
}
